/**
 * Sales Portal Layout Component
 * ==============================
 * Main layout for sales portal with consistent branding
 *
 * Features:
 * - Top header with logo and user menu
 * - Sidebar navigation
 * - Main content area
 * - Matches restaurant dashboard design language
 *
 * Design:
 * - Blue gradient background (matches main dashboard)
 * - Dine AI yellow (#FDB913) for accents
 * - Framer Motion animations
 * - Responsive layout
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  FileText,
  Upload,
  Users,
  Settings,
  LogOut,
  Menu as MenuIcon,
  X,
  ChevronDown,
  User,
  Shield,
  History,
  Phone
} from 'lucide-react';
import Logo from '@/components/Logo';
import { useSalesAuth } from '@/contexts/SalesAuthContext';

interface SalesLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  adminOnly?: boolean;
}

const SalesLayout: React.FC<SalesLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useSalesAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Navigation items
  const navItems: NavItem[] = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/sales-horse/dashboard' },
    { icon: FileText, label: 'Sessions', path: '/sales-horse/sessions' },
    { icon: Upload, label: 'New Upload', path: '/sales-horse/upload' },
    { icon: History, label: 'Upload History', path: '/sales-horse/upload-history' },
    { icon: Users, label: 'User Management', path: '/sales-horse/admin/users', adminOnly: true },
    { icon: Phone, label: 'Call Analyzer', path: '/sales-horse/admin/call-analyzer', adminOnly: true },
    { icon: Settings, label: 'Settings', path: '/sales-horse/settings' },
  ];

  // Filter nav items based on user role
  const filteredNavItems = navItems.filter(item =>
    !item.adminOnly || user?.role === 'admin'
  );

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/sales-horse');
  };

  const userMenuItems = [
    { icon: User, label: 'Profile', action: () => navigate('/sales-horse/settings') },
    { icon: LogOut, label: 'Logout', action: handleLogout },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen">
        {/* Header */}
        <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Left: Logo and Title */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  className="lg:hidden p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  {isSidebarOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
                </button>

                <Logo className="text-white h-8" />

                <div className="h-6 w-px bg-white/20"></div>

                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-semibold text-white">Sales Portal</h1>
                  {user?.role === 'admin' && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-[#FDB913]/20 border border-[#FDB913]/30 rounded text-[#FDB913] text-xs font-medium">
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                </div>
              </div>

              {/* Right: User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                >
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-medium">
                      {user?.first_name} {user?.last_name}
                    </span>
                    <span className="text-xs text-white/60">{user?.email}</span>
                  </div>

                  <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-medium">
                    {user?.first_name && user?.last_name
                      ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
                      : 'U'}
                  </div>

                  <ChevronDown className="w-4 h-4 text-white/60" />
                </button>

                {/* User Dropdown Menu */}
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2"
                    >
                      {userMenuItems.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            item.action();
                            setIsUserMenuOpen(false);
                          }}
                          className="flex items-center gap-3 w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <item.icon className="w-4 h-4" />
                          <span className="text-sm">{item.label}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Main Layout: Sidebar + Content */}
        <div className="flex">
          {/* Sidebar */}
          <AnimatePresence mode="wait">
            {isSidebarOpen && (
              <motion.aside
                initial={{ x: -280, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -280, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white/5 backdrop-blur-sm border-r border-white/10 mt-[73px] lg:mt-0"
              >
                <nav className="p-4 space-y-2">
                  {filteredNavItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => {
                          // Close sidebar on mobile after navigation
                          if (window.innerWidth < 1024) {
                            setIsSidebarOpen(false);
                          }
                        }}
                        className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                          ${isActive
                            ? 'bg-[#FDB913] text-black font-medium shadow-lg'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                          }
                        `}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-sm">{item.label}</span>
                      </Link>
                    );
                  })}
                </nav>
              </motion.aside>
            )}
          </AnimatePresence>

          {/* Mobile Sidebar Overlay */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {/* Content Area */}
          <main className="flex-1 min-h-[calc(100vh-73px)]">
            <div className="p-6">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default SalesLayout;
